import { useNavigate } from "react-router-dom";
import {
  getProductDetailPath,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Avatar,
  AvatarFallback,
  Button,
} from "@b2b/ui-components";
import { Clock, Eye } from "lucide-react";
import { AdminProduct } from "../../types/product.types";

interface RecentActivityProps {
  products: AdminProduct[];
  role?: "admin" | "seller";
  isLoading?: boolean;
}

export function RecentActivity({ products, role = "admin", isLoading = false }: RecentActivityProps) {
  const navigate = useNavigate();
  const isAdmin = role === "admin";

  // Get recent products (last 10, sorted by updatedAt)
  const recentProducts = products
    .slice()
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 10);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-10 w-10 bg-gray-200 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recentProducts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recentProducts.map((product) => {
            const statusStyle = {
              pending: "bg-yellow-100 text-yellow-700",
              approved: "bg-green-100 text-green-700",
              rejected: "bg-red-100 text-red-700",
            }[product.approvalStatus] || "bg-gray-100 text-gray-700";

            const sellerName = product.assignedSeller
              ? `${product.assignedSeller.firstName} ${product.assignedSeller.lastName}`
              : "Unassigned";

            return (
              <div
                key={product.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(getProductDetailPath(product))}
              >
                <Avatar className="h-10 w-10 rounded-lg">
                  <AvatarFallback className="bg-orange-100 text-orange-500 rounded-lg">
                    {product.name
                      .split(" ")
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium truncate">{product.name}</span>
                    <Badge className={`text-xs ${statusStyle}`}>
                      {product.approvalStatus}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <span>SKU: {product.sku}</span>
                    {isAdmin && (
                      <>
                        <span>•</span>
                        <span>{sellerName}</span>
                      </>
                    )}
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(product.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(getProductDetailPath(product));
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="View Details"
                >
                  <Eye className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t">
          <Button
            type="button"
            variant="ctaPrimary"
            className="w-full"
            onClick={() => navigate(isAdmin ? "/admin/products/list" : "/seller/products/list")}
          >
            View All Products →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

